import React, {useState} from "react";
import {useParams, useLocation, useNavigate, Navigate} from "react-router-dom";
import {useQuery} from "react-query";
import axios from "axios";
import moment from "moment";
import styled from "styled-components";
import {StatusCard} from "../../../components/StatusCard";
import {EmptyState} from "../../../components/EmptyState";
import PackageDeliveryAlert from "../../../components/PackageDeliveryAlert";
import AppLayout from "../../../layouts/AdminLayout";
import {Dialog, Transition} from "@headlessui/react";
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {getStatusType} from "../../../utils/status";
import toast from "react-hot-toast";
import {useRoles} from "../../../hooks/useRoles";

interface OrderData {
  orderId: string;
  orderStatus: string;
  deliveryType: string;
  TopupRequest: {
    id: string;
    amount: number;
    weightDiff: number;
    orderId: string;
    paid: boolean;
    createdAt: string;
    updatedAt: string;
  } | null;

  pickUpDetail: {
    fullAddress: string;
    phoneNumber: string;
    senderName: string;
    riderInfo?: {
      fullName: string;
      phoneNumber: string;
    };
    timelines?: Array<{
      event: string;
      entriedAt: string;
    }>;
  };
  deliveryDetails: Array<{
    lat: string;
    long: string;
    fullAddress: string;
    state: string;
    city: string;
    deliverySchedule: string;
    phoneNumber: string;
    recipientName: string;
    itemWeight: string;
    itemDescription: string;
    refId: string;
    status: string;
    latlng: string;
    additionalDetails: string | null;
    jobId: number;
    trackingLink: string;
    jobToken: string;
    jobHash: string;
    deliveryDate: string;
    timezone: string;
    deliveryType: string;
    recipientEmail: string;
    waybillNumber: string;
    note: string;
    timelines: Array<{
      event: string;
      entriedAt: string;
    }>;
    thirdPartyDispatchInfo?: {
      name: string;
      waybillNumber: string;
    };

    riderInfo: {
      fullName: string;
      phoneNumber: string;
    };
  }>;
}

export const DeliveryDetails = () => {
  const {scope} = useRoles();
  const {orderID} = useParams<{orderID: string}>();
  const {waybillNO} = useParams<{waybillNO: string}>();
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;
  const [isLoadingPackage, setIsLoadingPackage] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [isTopupSuccessOpen, setIsTopupSucessOpen] = useState(true);
  const [isDeliveryStatusOpen, setIsDeliveryStatusOpen] = useState(false);
  const [isDeliveryStatusUpdating, setIsDeliveryStatusUpdating] =
    useState(false);
  const [selectedDeliveryStatus, setSelectedDeliveryStatus] = useState("");

  const [type, setType] = useState("weight_diff");
  const [newWeightDifference, setNewWeightDifference] = useState("");
  const [reason, setReason] = useState("");

  const handleTopUpAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTopUpAmount(event.target.value);
  };

  const handleNewWeightDifferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewWeightDifference(event.target.value);
  };
  const handlereasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
  };
  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  const updateDeliveryOrderStatus = async (status: string, refId: string) => {
    if (!status) {
      toast.error("Please select a status");
      return;
    }

    setIsDeliveryStatusUpdating(true);
    try {
      await axios.patch(
        `${Baseurl}/api/admin/orders/${orderID}/deliveries/${refId}/status`,
        {status},
        {
          ...config,
        }
      );
      toast.success("Order status updated successfully");
      setIsDeliveryStatusOpen(false);
      setSelectedDeliveryStatus(""); // Reset selection
      refetch();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message;

      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]);
      } else if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else {
        toast.error("Failed to update order status");
      }
    } finally {
      setIsDeliveryStatusUpdating(false);
    }
  };

  const fetchOrder = async () => {
    const response = await axios.get(
      `${Baseurl}/api/admin/orders/${orderID}/deliveries/${waybillNO}`,
      config
    );
    return response?.data?.data;
  };

  const {
    data: orderData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery<OrderData>(["SINGLE_ORDER", orderID], fetchOrder, {
    enabled: !!orderID,
    cacheTime: 0,
  });

  const status = orderData
    ? getStatusType(orderData.deliveryDetails[0]?.status)
    : {title: "", type: ""};

  const firstDeliveryDetail = orderData?.deliveryDetails?.[0] || {
    trackingLink: undefined,
    fullAddress: undefined,
    phoneNumber: undefined,
    recipientName: undefined,
    itemWeight: undefined,
    itemDescription: undefined,
    additionalDetails: undefined,
    deliveryDate: undefined,
    timelines: undefined,
    waybillNumber: undefined,
    thirdPartyDispatchInfo: undefined,
    status: undefined,
    note: undefined,
    riderInfo: undefined,
    recipientEmail: undefined,
  };
  const pickUpDetail = orderData?.pickUpDetail || {
    fullAddress: undefined,
    phoneNumber: undefined,
    senderName: undefined,
    riderInfo: undefined,
    timelines: undefined,
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const queueDelivery = async () => {
    if (Number(topUpAmount) < 1 || topUpAmount === "" || !topUpAmount) {
      toast.error("Provide topup amount to continue.");
      return;
    }
    if (type === "reason" || type === "both") {
      if (reason === "" || reason === null || reason === undefined) {
        toast.error("Provide a reason to continue.");
        return;
      }
    }

    if (type === "weight_diff" || type === "both") {
      if (
        newWeightDifference === "" ||
        newWeightDifference === null ||
        newWeightDifference === undefined
      ) {
        toast.error("Provide weight difference to continue.");
        return;
      }
    }

    setIsRequesting(true);
    try {
      await axios.patch(
        `${Baseurl}/api/admin/orders/${orderID}/topups/queue`,
        {
          amount: String(topUpAmount),
          weightDiff:
            type === "weight_diff" || type === "both"
              ? String(newWeightDifference)
              : undefined,
          waybillNumber: waybillNO,
          type: type,
          reason: type === "reason" || type === "both" ? reason : undefined,
        },
        {
          ...config,
        }
      );
      toast.success("Item added to queue!");
      refetch();

      setIsOpen(false);
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsRequesting(false);
    }
  };

  const processRefund = async () => {
    setIsRequesting(true);
    try {
      await axios.patch(
        `${Baseurl}/api/admin/orders/${orderID}/items/${firstDeliveryDetail.waybillNumber}/refund/initiate`,
        {},
        {
          ...config,
        }
      );
      refetch();
    } catch (error) {
    } finally {
      setIsRequesting(false);
    }
  };
  const confirmRefund = async () => {
    setIsRequesting(true);
    try {
      await axios.patch(
        `${Baseurl}/api/admin/orders/${orderID}/items/${firstDeliveryDetail.waybillNumber}/refund/complete`,
        {},
        {
          ...config,
        }
      );
      refetch();
    } catch (error) {
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <AppLayout>
      {isLoading ? (
        <div className="p-10 text-center text-gray-500">Loading...</div>
      ) : !orderData ? (
        <EmptyState text="Order not found" type="" />
      ) : (
        <div className="p-5">
          <div className="flex !items-center !justify-between !p-4">
            <button
              className="flex items-center gap-2 !mb-6"
              onClick={() => navigate(-1)}
            >
              <ArrowLeftIcon width={24} height={24} color="#666D80" />
              <p className="!text-sm text-[#666d80]">Go Back</p>
            </button>
            {/* <button>
			  <XMarkIcon width={24} height={24} color="#666D80" />
			</button> */}
          </div>

          <div className="flex justify-between items-center mb-5">
            <h2 className="text-3xl font-bold">Delivery Details</h2>

            <div className="flex items-center gap-4">
              {(orderData.orderStatus === "IN_HUB" ||
                orderData.orderStatus === "EXECUTING") &&
                //   firstDeliveryDetail?.status === "UNASSIGNED" &&
                scope === "write" && (
                  <HubButton
                    onClick={() => setIsOpen(true)}
                    className="!border !border-[#fe7f1f] !rounded-[10px] !bg-transparent !text-[#fe7f1f] !font-bold !py-3 !px-5 !text-sm"
                  >
                    Request Topup
                  </HubButton>
                )}
              <HubButton
                onClick={() => {
                  setIsDeliveryStatusOpen(true);
                }}
                className="!border !border-[#fe7f1f] !rounded-[10px] !bg-transparent !text-[#fe7f1f] !font-bold !py-3 !px-5 !text-sm"
              >
                Change Delivery Status
              </HubButton>
            </div>
          </div>

          {orderData?.TopupRequest?.paid && isTopupSuccessOpen && (
            <div className="!flex !items-center !justify-between !gap-3 !w-full !rounded-lg !bg-green-600/20 !p-4 !my-4 !border-l-4 !border-green-600">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon
                  className="!w-5 !h-5 !text-green-600"
                  aria-hidden="true"
                />
                <p className="!text-green-600">Top up payment successful</p>
              </div>
              {scope === "write" && (
                <button
                  className="text-xs font-semibold !text-green-600"
                  onClick={() => setIsTopupSucessOpen(false)}
                >
                  Close
                </button>
              )}
            </div>
          )}

          {orderData?.TopupRequest &&
            firstDeliveryDetail.status === "CANCELLED" && (
              <div className="!flex !items-center !justify-between !gap-3 !w-full !rounded-lg !bg-[#FEF3F2] !p-4 !my-4 !border-l-4 !border-l-[#F04438]">
                <div className="!flex !items-center  !gap-3">
                  <ExclamationTriangleIcon
                    className="!w-5 !h-5 !text-[#F04438]"
                    aria-hidden="true"
                  />
                  <p className="">Top up has been rejected</p>
                </div>
                {scope === "write" && (
                  <button
                    className="py-4 px-6 rounded-[10px] bg-[#ffeed4] border border-[#fe7f1f] text-[#fe7f1f] font-bold text-sm"
                    onClick={() => processRefund()}
                  >
                    {isRequesting ? "Loading.." : "Process refund"}
                  </button>
                )}
              </div>
            )}

          {orderData?.TopupRequest &&
            firstDeliveryDetail.status === "REFUND_INITIATED" && (
              <div className="!flex !items-center !justify-between !gap-3 !w-full !rounded-lg !bg-green-400/20 !p-4 !my-4 !border-l-4 !border-l-green-400">
                <div className="!flex !items-center  !gap-3">
                  <ExclamationTriangleIcon
                    className="!w-5 !h-5 !text-green-400"
                    aria-hidden="true"
                  />
                  <p className="">Confirm refund</p>
                </div>
                {scope === "write" && (
                  <button
                    className="py-4 px-6 rounded-[10px] bg-white border border-green-400 text-green-400 font-bold text-sm"
                    onClick={() => confirmRefund()}
                  >
                    {isRequesting ? "Loading.." : "Complete refund"}
                  </button>
                )}
              </div>
            )}

          <OrderHeader>
            <OrderInfo>
              <div>
                <InfoLabel>Waybill NO:</InfoLabel>
                <OrderId>{waybillNO}</OrderId>
              </div>
              <StatusContainer>
                <StatusCard title={status.title} type={status.type} />
                <RefreshIcon
                  className={`${isRefetching ? "animate-spin" : ""}`}
                  onClick={() => refetch()}
                  src="/refresh.svg"
                  alt="Refresh"
                />
              </StatusContainer>
            </OrderInfo>

            <ActionButtons>
              {firstDeliveryDetail?.trackingLink && (
                <div className="relative w-full overflow-x-auto whitespace-nowrap text-sm text-gray-700 pr-10">
                  <span className=" border-gray-100 rounded-lg">
                    Tracking Link:{" "}
                    <strong className="text-black">
                      {firstDeliveryDetail.trackingLink}
                    </strong>
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(firstDeliveryDetail.trackingLink!)
                    }
                    className="absolute top-1/2 right-0 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  >
                    <img src="/copy.svg" alt="Copy" className="w-4 h-4" />
                  </button>
                </div>
              )}
            </ActionButtons>
          </OrderHeader>

          <GridContainer>
            <TrackPackageCard>
              <TrackHeader>
                <img src="/truck.svg" alt="Truck" />
                <p>Track your package delivery</p>
              </TrackHeader>

              <AddressDisplay>
                <AddressTag>
                  {pickUpDetail?.fullAddress?.substring(0, 14) || "N/A"}
                  {(pickUpDetail?.fullAddress?.length ?? 0) > 14 && "..."}
                </AddressTag>
                <DeliveryLine
                  src="/delivery-line-spoke.svg"
                  alt="Delivery line"
                />
                <AddressTag>
                  {firstDeliveryDetail?.fullAddress?.substring(0, 14) || "N/A"}
                  {(firstDeliveryDetail?.fullAddress?.length ?? 0) > 14 &&
                    "..."}
                </AddressTag>
              </AddressDisplay>

              <ProgressBar>
                <ProgressFill
                  width={
                    orderData?.orderStatus === "PROCESSING"
                      ? "15%"
                      : orderData?.orderStatus === "ENROUTE_TO_PICKUP"
                      ? "20%"
                      : orderData?.orderStatus === "PICKEDUP"
                      ? "25%"
                      : orderData?.orderStatus === "PICKUP_FAILED"
                      ? "25%"
                      : orderData?.orderStatus === "PACKAGE_AT_HUB"
                      ? "50%"
                      : orderData?.orderStatus === "IN_HUB"
                      ? "50%"
                      : orderData?.orderStatus === "EXECUTING"
                      ? "75%"
                      : orderData?.orderStatus === "COMPLETED"
                      ? "100%"
                      : "10%"
                  }
                >
                  <DeliveryIcon src="/delivery.svg" alt="Delivery" />
                </ProgressFill>
              </ProgressBar>
            </TrackPackageCard>

            <InfoCard>
              <img src="/time.svg" alt="Time" className="w-6 h-6" />
              <InfoText>
                <InfoLabelSmall>Estimated Arrival</InfoLabelSmall>
                <InfoValue>
                  {firstDeliveryDetail?.deliveryDate
                    ? moment(firstDeliveryDetail.deliveryDate).format(
                        "DD MMM, YYYY"
                      )
                    : "N/A"}
                </InfoValue>
              </InfoText>
            </InfoCard>

            <InfoCard>
              <RiderHeader>
                <img src="/motorcycle.svg" alt="Motorcycle" />
                {/* <ContactButton className="!flex !justify-center !items-center !p-2 !gap-2.5 !ml-auto !text-sm !font-semibold !bg-white !border !border-orange-500 !text-orange-500 !rounded-[35px]">
				  Contact Rider
				</ContactButton> */}
              </RiderHeader>
              <InfoText>
                <InfoLabelSmall>Rider Information</InfoLabelSmall>
                <InfoValue>
                  {firstDeliveryDetail.riderInfo?.fullName || "N/A"}
                </InfoValue>
                <InfoValueSmall>
                  {firstDeliveryDetail.riderInfo?.phoneNumber || "N/A"}
                </InfoValueSmall>
              </InfoText>
            </InfoCard>
          </GridContainer>

          <DetailsGrid>
            <DetailCard>
              <DetailTitle>Delivery Information</DetailTitle>
              <DetailContent>
                <DetailTextBold>
                  {firstDeliveryDetail?.recipientName}
                </DetailTextBold>
                <DetailText>{firstDeliveryDetail?.fullAddress}</DetailText>
                <DetailText>{firstDeliveryDetail?.phoneNumber}</DetailText>
                <DetailText>
                  {firstDeliveryDetail?.recipientEmail || ""}
                </DetailText>
                <p className="text-sm">
                  {firstDeliveryDetail.note &&
                    `Note: ${firstDeliveryDetail.note}`}
                </p>

                {firstDeliveryDetail.thirdPartyDispatchInfo && (
                  <>
                    <DetailTitle>Dispatch Information</DetailTitle>

                    <DetailText>
                      Dispatch Name:{" "}
                      <strong>
                        {firstDeliveryDetail.thirdPartyDispatchInfo?.name!}
                      </strong>
                    </DetailText>
                    <DetailText>
                      Tracking no:{" "}
                      <strong>
                        {
                          firstDeliveryDetail.thirdPartyDispatchInfo
                            ?.waybillNumber!
                        }
                      </strong>
                    </DetailText>
                  </>
                )}
                <DetailDivider />
                <DetailText>
                  Product Weight -{" "}
                  <strong>{firstDeliveryDetail?.itemWeight}</strong>
                </DetailText>
                <DetailText>
                  {firstDeliveryDetail?.itemDescription}
                  {firstDeliveryDetail?.additionalDetails &&
                    `(${firstDeliveryDetail.additionalDetails})`}
                </DetailText>
              </DetailContent>
            </DetailCard>

            <div className="!py-5 !px-8 !border !border-black/10 !rounded-[10px]">
              <p className="!text-sm !text-gray-400 !font-semibold !mb-6">
                Delivery Timeline
              </p>
              {[0, 1, 2].map((step) => {
                const timeline = firstDeliveryDetail?.timelines?.[step];
                const isFailed =
                  (step === 2 || step === 1) &&
                  timeline?.event?.includes("failed");

                return (
                  <div
                    className="!flex !justify-between !items-center !mb-7"
                    key={`delivery-step-${step}`}
                  >
                    <div className="!flex !gap-6 !items-center">
                      <div
                        className={`!text-xs !min-w-[90px] ${
                          timeline ? "!text-gray-700" : "!text-gray-200"
                        } !font-semibold`}
                      >
                        <p>
                          {timeline?.entriedAt
                            ? moment(timeline.entriedAt).format("DD MMM, YYYY")
                            : "D MMM, YYYY"}
                        </p>
                        <p>
                          {timeline?.entriedAt
                            ? moment(timeline.entriedAt).format("HH:mm")
                            : "12:00"}
                        </p>
                      </div>
                      <div
                        className={`${
                          timeline ? "!text-gray-700" : "!text-gray-200"
                        } !font-semibold`}
                      >
                        <p className="!text-sm">
                          {timeline?.event ||
                            (step === 0
                              ? "Rider enroute to delivery location"
                              : step === 1
                              ? "Rider at drop-off location"
                              : "Package delivered")}
                        </p>
                        <p className="!text-[10px] !text-gray-400">
                          {firstDeliveryDetail?.fullAddress || ""}
                        </p>
                      </div>
                    </div>
                    {timeline && (
                      <img
                        src={
                          isFailed ? "/tick-failed.svg" : "/tick-success.svg"
                        }
                        alt={isFailed ? "Failed" : "Success"}
                      />
                    )}
                  </div>
                );
              })}
              {/* Additional delivery timelines if they exist */}
              {firstDeliveryDetail.timelines &&
                firstDeliveryDetail?.timelines?.length > 3 &&
                firstDeliveryDetail.timelines.slice(3).map((timeline, idx) => {
                  const isFailed = timeline.event?.includes("failed");

                  return (
                    <div
                      className="!flex !justify-between !items-center !mb-7"
                      key={`additional-delivery-${idx}`}
                    >
                      <div className="!flex !gap-6 !items-center">
                        <div className="!text-xs !min-w-[90px] !text-gray-700 !font-semibold">
                          <p>
                            {moment(timeline.entriedAt).format("DD MMM, YYYY")}
                          </p>
                          <p>{moment(timeline.entriedAt).format("HH:mm")}</p>
                        </div>
                        <div className="!text-gray-700 !font-semibold">
                          <p className="!text-sm">{timeline.event}</p>
                          <p className="!text-[10px] !text-gray-400">
                            {firstDeliveryDetail?.fullAddress || ""}
                          </p>
                        </div>
                      </div>
                      <img
                        src={
                          isFailed ? "/tick-failed.svg" : "/tick-success.svg"
                        }
                        alt={isFailed ? "Failed" : "Success"}
                      />
                    </div>
                  );
                })}
            </div>
          </DetailsGrid>

          <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog
              as="div"
              className="relative z-50"
              onClose={() => setIsOpen(false)}
            >
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/20 bg-opacity-25" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden flex items-center justify-end p-4">
                  <div className="pointer-events-none flex max-w-[513px] h-full overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
                    <Transition.Child
                      as={React.Fragment}
                      enter="transform transition ease-in-out duration-500 sm:duration-700"
                      enterFrom="translate-x-full"
                      enterTo="translate-x-0"
                      leave="transform transition ease-in-out duration-500 sm:duration-700"
                      leaveFrom="translate-x-0"
                      leaveTo="translate-x-full"
                    >
                      <Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col justify-between overflow-hidden">
                        <div className="!p-6">
                          <div className="flex items-center justify-between">
                            <p className="!text-xl !font-bold !text-[#272835]">
                              Request Top Up
                            </p>
                          </div>
                          <p className="mt-2 !text-sm text-gray-400">
                            Provide details about top up request
                          </p>
                          <div className="!mt-4">
                            <div>
                              <p className="!text-gray-600 !text-sm !mb-2">
                                Type
                              </p>
                              <select
                                name="type"
                                id="type"
                                onChange={(
                                  e: React.ChangeEvent<
                                    | HTMLInputElement
                                    | HTMLSelectElement
                                    | HTMLTextAreaElement
                                  >
                                ) => {
                                  setType(e.target.value);
                                }}
                                className="!h-[50px] !border !border-[#dfe1e7] !py-2 !px-3 !rounded-[10px] !w-full mb-4"
                              >
                                <option value="weight_diff">
                                  Weight Difference
                                </option>
                                <option value="reason">Miscellaneous </option>
                                <option value="both">Both</option>
                              </select>
                            </div>

                            <div>
                              <p className="!text-gray-600 !text-sm !mb-2">
                                Top Up Amount
                              </p>
                              <input
                                type="text"
                                name="topUpAmount"
                                id="topUpAmount"
                                placeholder="Enter amount here"
                                className="!h-[50px] !border !border-[#dfe1e7] !py-4 !px-6 !rounded-[10px] !w-full mb-4"
                                value={topUpAmount}
                                onChange={handleTopUpAmountChange}
                              />
                            </div>
                            {(type === "weight_diff" || type === "both") && (
                              <div>
                                <p className="!text-gray-600 !text-sm !mb-2">
                                  New Weight Difference
                                </p>
                                <input
                                  type="number"
                                  name="newWeightDifference"
                                  id="newWeightDifference"
                                  placeholder="Enter weight here"
                                  className="!h-[50px] !border !border-[#dfe1e7] !py-4 !px-6 !rounded-[10px] !w-full mb-4"
                                  value={newWeightDifference}
                                  onChange={handleNewWeightDifferenceChange}
                                />
                              </div>
                            )}
                            {(type === "reason" || type === "both") && (
                              <div>
                                <p className="!text-gray-600 !text-sm !mb-2">
                                  Reason
                                </p>
                                <input
                                  type="text"
                                  name="reason"
                                  id="reason"
                                  placeholder="Enter reason"
                                  className="!h-[50px] !border !border-[#dfe1e7] !py-4 !px-6 !rounded-[10px] !w-full mb-4"
                                  value={reason}
                                  onChange={handlereasonChange}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Header */}
                        <div className="flex items-center !gap-4 justify-between !p-6">
                          <button
                            onClick={() => setIsOpen(false)}
                            className="!bg-[#fff0f3] !rounded-[10px] !font-bold !py-4 !px-[41px] !flex !items-center !justify-center !text-[#FF3B30]"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => queueDelivery()}
                            className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white"
                          >
                            {isRequesting ? "Loading..." : "Add to queue"}
                          </button>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </div>
            </Dialog>
          </Transition>

          <Transition appear show={isDeliveryStatusOpen} as={React.Fragment}>
            <Dialog
              as="div"
              className="relative z-50"
              onClose={() => setIsDeliveryStatusOpen(false)}
            >
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/20 bg-opacity-25" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
                  <div className="pointer-events-none flex max-w-[513px] h-max overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
                    <Transition.Child
                      as={React.Fragment}
                      enter="transform transition ease-in-out duration-500 sm:duration-700"
                      enterFrom="translate-x-full"
                      enterTo="translate-x-0"
                      leave="transform transition ease-in-out duration-500 sm:duration-700"
                      leaveFrom="translate-x-0"
                      leaveTo="translate-x-full"
                    >
                      <Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col justify-between overflow-hidden">
                        <div className="!p-6">
                          <p className="!text-xl !font-bold !text-[#272835] text-center">
                            Change Delivery Status
                          </p>
                        </div>

                        <div className="!px-6 !pb-6">
                          <select
                            className="select w-full px-3 border border-gray-300 rounded-[10px] focus:border-orange-500 focus:ring-orange-500 text-sm"
                            onChange={(e) =>
                              setSelectedDeliveryStatus(e.target.value)
                            }
                            value={selectedDeliveryStatus}
                          >
                            <option value="">Select Status</option>
                            <option value="ASSIGNED">Assigned</option>
                            <option value="UNASSIGNED">Unassigned</option>
                            <option value="DELIVERED">Delivered</option>
                            <option value="FAILED">Failed</option>
                            <option value="PICKEDUP">Picked Up</option>
                            <option value="IN_TRANSIT">In Transit</option>
                            <option value="TOPUP_REQUESTED">
                              Top up Requested
                            </option>
                            <option value="REFUND_INITIATED">
                              Refund Initiated
                            </option>
                            <option value="REFUND_COMPLETED">
                              Refund Completed
                            </option>
                          </select>
                        </div>

                        <div className="flex items-center !gap-4 justify-between !p-6">
                          <button
                            onClick={() =>
                              updateDeliveryOrderStatus(
                                selectedDeliveryStatus,
                                orderData.deliveryDetails[0].refId
                              )
                            }
                            disabled={
                              !selectedDeliveryStatus ||
                              isDeliveryStatusUpdating
                            }
                            className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-1/2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isDeliveryStatusUpdating
                              ? "Updating..."
                              : "Update Status"}
                          </button>
                          <button
                            onClick={() => setIsDeliveryStatusOpen(false)}
                            className="border !border-[#FF3B30] !rounded-[10px] !font-bold !py-4 !px-[41px] !flex !items-center !justify-center !text-[#FF3B30] w-1/2"
                          >
                            Cancel
                          </button>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </div>
            </Dialog>
          </Transition>
        </div>
      )}
    </AppLayout>
  );
};

const OrderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const OrderInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 40px;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshIcon = styled.img`
  margin-top: 4px;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #818898;
`;

const OrderId = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #272835;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin: 48px 0 24px;
`;

const TrackPackageCard = styled.div`
  background-color: #f4f6fa;
  border-radius: 10px;
  padding: 24px;
  grid-column: span 2;
`;

const TrackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 40px;

  p {
    color: #666d80;
    font-weight: 600;
  }
`;

const AddressDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
`;

const AddressTag = styled.p`
  color: white;
  font-size: 11px;
  font-weight: 600;
  background-color: black;
  padding: 8px 20px;
  border-radius: 35px;
  white-space: nowrap;
`;

const DeliveryLine = styled.img`
  max-width: 190px;
  transform: scale(0.83);
`;

const ProgressBar = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 10px;
  height: 8px;
  display: flex;
  align-items: center;
  padding: 0 2px;
`;

const ProgressFill = styled.div<{width: string}>`
  background-color: #31eaf4;
  height: 4px;
  border-radius: 10px;
  position: relative;
  width: ${(props) => props.width};
`;

const DeliveryIcon = styled.img`
  position: absolute;
  bottom: -4px;
  right: 0;
`;

const InfoCard = styled.div`
  padding: 32px 24px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoLabelSmall = styled.p`
  font-size: 12px;
  color: #818898;
`;

const InfoValue = styled.p`
  font-size: 18px;
  color: #272835;
  font-weight: 600;
`;

const InfoValueSmall = styled.p`
  font-size: 10px;
  color: #818898;
`;

const RiderHeader = styled.div`
  display: flex;
  align-items: center;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`;

const DetailCard = styled.div`
  padding: 20px 32px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
`;

const TimelineCard = styled(DetailCard)`
  grid-row: span 1;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const DetailTitle = styled.p`
  font-size: 14px;
  color: #818898;
  font-weight: 600;
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailText = styled.p`
  font-size: 14px;
  color: #272835;
  margin-bottom: 4px;
`;

const DetailTextBold = styled(DetailText)`
  font-weight: 700;
`;

const DetailDivider = styled.div`
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.1);
`;
const HubButton = styled.button`
  padding: 16px 28px;
  background: #fe7f1f;
  border-radius: 10px;
  color: white;
  font-weight: bold;
  border: none;
  cursor: pointer;
`;
